import { Table } from "../../../Components/Table/Table";
import React, { useMemo, useState, useEffect } from "react";
import toast from "react-hot-toast";
import { adminOverallReportAPI } from "../../../api/attendance";
import { useNavigate } from "react-router-dom";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";

const AdminAttendance = () => {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");

  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");

  // ✅ CURRENT DATE
  const today = new Date();
  const currentMonth = today.getMonth() + 1;
  const currentYear = today.getFullYear();
  const currentMonthName = today.toLocaleString("default", {
    month: "long",
  });

  // ✅ SET DEFAULT CURRENT MONTH
  useEffect(() => {
    setSelectedMonth(currentMonth);
    setSelectedYear(currentYear);
  }, []);

  // ✅ FETCH DATA
  const fetchAttendance = async () => {
    try {
      setLoading(true);

      const res = await adminOverallReportAPI({
        search,
        status: statusFilter,
        month: selectedMonth,
        year: selectedYear,
      });

      setEmployees(res.data || []);
    } catch (error) {
      toast.error("Failed to load attendance report");
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ AUTO CALL API
  useEffect(() => {
    if (selectedMonth && selectedYear) {
      fetchAttendance();
    }
  }, [search, statusFilter, selectedMonth, selectedYear]);

  // ✅ TABLE DATA
  const tableData = useMemo(() => {
    const monthNames = [
      "Jan","Feb","Mar","Apr","May","Jun",
      "Jul","Aug","Sep","Oct","Nov","Dec"
    ];

    return employees.map((emp) => ({
      employeeid: emp.employeeId,
      name: emp.name,
      email: emp.email,

      dayspayable: emp.daysPayable,
      daysinmonth: emp.daysInMonth,

      month: monthNames[(emp.month || 1) - 1],
      year: emp.year,

      totalPresent: emp.present ?? 0,
      totalAbsent: (emp.absent ?? 0) + (emp.late ?? 0),
      halfDay: emp.halfDay ?? 0,
    }));
  }, [employees]);

  // ✅ TABLE COLUMNS
  const columns = useMemo(() => [
    {
      Header: "S.No",
      Cell: ({ row }) => <span>{row.index + 1}</span>,
    },
    {
      Header: "Employee ID",
      accessor: "employeeid",
    },
    {
      Header: "Name",
      accessor: "name",
    },
    {
      Header: "Email",
      accessor: "email",
    },
    {
      Header: "Days Payable",
      accessor: "dayspayable",
    },
    {
      Header: "Days in Month",
      accessor: "daysinmonth",
    },
    {
      Header: "Month",
      accessor: "month",
    },
    {
      Header: "Year",
      accessor: "year",
    },
    {
      Header: "Total Present",
      accessor: "totalPresent",
    },
    {
      Header: "Total Absent",
      accessor: "totalAbsent",
    },
    {
      Header: "Half Day",
      accessor: "halfDay",
    },
    {
      Header: "Actions",
      Cell: ({ row }) => (
        <div className="action-icons">
          <RemoveRedEyeIcon
            onClick={() =>
              navigate(`/employee-login-history/${row.original.employeeid}`)
            }
            style={{ cursor: "pointer" }}
          />
        </div>
      ),
    },
  ], [navigate]);

  return (
    <div className="container-admin">
      <div className="top-section">
        <div className="filters">

          {/* 🔍 SEARCH */}
          <input
            type="text"
            placeholder="Search employee..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {/* 📅 MONTH */}
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            <option value="">Month</option>
            {[...Array(12)].map((_, i) => (
              <option key={i + 1} value={i + 1}>
                {new Date(0, i).toLocaleString("default", { month: "short" })}
              </option>
            ))}
          </select>

          {/* 📅 YEAR */}
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            <option value="">Year</option>
            {[2023, 2024, 2025, 2026].map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>

          {/* 📊 STATUS */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All</option>
            <option value="Present">Present</option>
            <option value="Absent">Absent</option>
            <option value="Late">Late</option>
            <option value="Half Day">Half Day</option>
          </select>

          {/* ✅ CURRENT MONTH BUTTON */}
          <button
            onClick={() => {
              setSelectedMonth(currentMonth);
              setSelectedYear(currentYear);
            }}
            style={{
              background:
                Number(selectedMonth) === currentMonth &&
                Number(selectedYear) === currentYear
                  ? "green"
                  : "#ccc",
              color: "#fff",
              border: "none",
              padding: "6px 12px",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            {currentMonthName}
          </button>

        </div>
      </div>

      <Table columns={columns} data={tableData} loading={loading} />
    </div>
  );
};

export default AdminAttendance;